import React, { useMemo, useState } from 'react';
import useTrackingStore from '../tracking/useTrackingStore';
import { normalizeAssetId } from '../tracking/trackingModel';
import { deploymentUsageItems, isLegacyBundleAsset, operationalAssets } from '../tracking/deploymentUsageModel';
import { buildLineEMTDeploymentCapacity, buildLineMedicDeploymentCapacity } from '../tracking/deploymentCapacityModel';
import {
  availableSeasonYears,
  buildGearStatus,
  buildSeasonSummary,
  deploymentDayCount,
} from '../tracking/deploymentDashboardModel';
import AssetHistory from './AssetHistory';
import DeploymentGearEditor from './DeploymentGearEditor';
import { downloadCsv, openPrintableReport } from './exportUtils';
import './admin.css';

function deploymentTypeLabel(deployment) {
  return deployment?.deploymentType || deployment?.role || 'EMPF';
}

function statusClass(value) {
  return String(value || 'available').toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

function returnDateLabel(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function dateTimeLabel(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

const LEGACY_DASHBOARD_TABS = ['live', 'gear', 'history', 'season'];

function initialDashboardTab() {
  const requested = new URLSearchParams(window.location.search).get('tab');
  const establishedTabs = ['live', 'gear', 'history', 'season'];
  if (requested === 'reports') return 'reports';
  return establishedTabs.includes(requested) ? requested : 'live';
}

function CapacityCard({ title, subtitle, capacity }) {
  return (
    <section className="admin-card">
      <div className="admin-section-head">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <div className="admin-day-pill">{capacity.capacity} ready</div>
      </div>
      {capacity.rows.map(row => (
        <div className="admin-table-row" key={row.category}>
          <span><strong>{row.label}</strong><small>{row.total} program assets in service</small></span>
          <strong>{row.ready} ready</strong>
        </div>
      ))}
      <div className="admin-deployment-meta">
        <span>Capacity is limited by: {capacity.limiting.join(', ') || 'No configured gear'}</span>
      </div>
    </section>
  );
}

function LiveTab({ state, onEditGear }) {
  const active = state.deployments.filter(item => item.status !== 'closed');
  const recentClosed = state.deployments
    .filter(item => item.status === 'closed' && item.returnedAt)
    .sort((a, b) => (Date.parse(b.returnedAt) || 0) - (Date.parse(a.returnedAt) || 0))
    .slice(0, 5);
  const assets = operationalAssets(state);
  const ready = assets.filter(item => item.status === 'available' && item.condition === 'ready').length;
  const oos = assets.filter(item => item.status === 'out_of_service' || item.condition === 'out_of_service').length;
  const needsAttention = assets.filter(item => ['needs_inventory', 'needs_restock', 'damaged'].includes(item.status)).length;
  const medicCapacity = buildLineMedicDeploymentCapacity(assets);
  const emtCapacity = buildLineEMTDeploymentCapacity(assets);

  return (
    <>
      <div className="admin-summary-grid">
        <div className="admin-stat"><span>Personnel Deployed</span><strong>{active.length}</strong></div>
        <div className="admin-stat"><span>EMPF Capacity</span><strong>{medicCapacity.capacity}</strong></div>
        <div className="admin-stat"><span>EMTF Capacity</span><strong>{emtCapacity.capacity}</strong></div>
        <div className="admin-stat"><span>Ready Gear</span><strong>{ready}</strong></div>
        <div className="admin-stat"><span>Needs Attention</span><strong>{needsAttention}</strong></div>
        <div className="admin-stat"><span>OOS</span><strong>{oos}</strong></div>
      </div>

      <div className="admin-two-column">
        <CapacityCard
          title="Line Medic / EMPF Capacity"
          subtitle="Complete program EMPF deployments available right now. Asset numbers do not need to match; capacity uses whichever physical gear is ready."
          capacity={medicCapacity}
        />
        <CapacityCard
          title="Line EMT / EMTF Capacity"
          subtitle="Complete EMTF deployments available right now. EMTF requires the BLS Pack, O2 Bag, Radio Antenna, Clipboard, and Combi Tool — no Trauma Bag or AED."
          capacity={emtCapacity}
        />
      </div>

      <section className="admin-card">
        <div className="admin-section-head"><div><h2>Active Deployments</h2><p>Live personnel, incidents, deployment age, gear, and reported consumption.</p></div></div>
        {!active.length && <div className="admin-empty">No active deployments.</div>}
        <div className="admin-deployment-list">
          {active.map(deployment => {
            const realAssetIds = (deployment.assetIds || []).map(normalizeAssetId).filter(id => !isLegacyBundleAsset(id));
            const assigned = realAssetIds.map(id => assets.find(asset => normalizeAssetId(asset.id) === id)).filter(Boolean);
            const usage = deploymentUsageItems(state, deployment.id);
            const usedQty = usage.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
            return (
              <article className="admin-deployment-card" key={deployment.id}>
                <div className="admin-deployment-title">
                  <div>
                    <strong>{deployment.person}</strong>
                    <span>{deployment.incident}{deployment.incidentOrderNumber ? ` · ${deployment.incidentOrderNumber}` : ''}</span>
                  </div>
                  <div className="admin-deployment-actions">
                    <button type="button" className="admin-inline-action" onClick={() => onEditGear(deployment)}>Edit Gear</button>
                    <div className="admin-day-pill">Day {deploymentDayCount(deployment)}</div>
                  </div>
                </div>
                <div className="admin-deployment-meta">
                  <span>{deploymentTypeLabel(deployment)}</span>
                  <span>{assigned.length} physical assets</span>
                  <span>{usedQty} supplies reported used</span>
                </div>
                <div className="admin-gear-tags">
                  {assigned.map(asset => <span key={asset.id}>{asset.name}</span>)}
                  {!assigned.length && <span className="admin-legacy-note">Legacy deployment record — no individual physical assets recorded</span>}
                </div>
                {usage.length > 0 && (
                  <details className="admin-usage-details">
                    <summary>Reported usage</summary>
                    {usage.map(item => <div key={`${item.assetId}-${item.catalogId}`}><span>{item.item}</span><strong>×{item.quantity}</strong><small>{item.assetName}</small></div>)}
                  </details>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-section-head"><div><h2>Recent Returns</h2><p>Most recently closed deployments and the condition of the gear that came back.</p></div></div>
        {!recentClosed.length && <div className="admin-empty">No completed deployment returns recorded yet.</div>}
        <div className="admin-deployment-list">
          {recentClosed.map(deployment => {
            const realAssetIds = (deployment.assetIds || []).map(normalizeAssetId).filter(id => !isLegacyBundleAsset(id));
            const returnedByAsset = new Map();
            (state.events || [])
              .filter(event => event?.type === 'asset_return' && event?.deploymentId === deployment.id)
              .forEach(event => {
                const id = normalizeAssetId(event.assetId);
                if (id && !returnedByAsset.has(id)) returnedByAsset.set(id, event);
              });
            const returnedCount = realAssetIds.filter(id => returnedByAsset.has(id)).length;
            const allReturned = realAssetIds.length > 0 && returnedCount === realAssetIds.length;
            const allReady = allReturned && realAssetIds.every(id => returnedByAsset.get(id)?.condition === 'ready');
            const returnedAssets = realAssetIds.map(id => assets.find(asset => normalizeAssetId(asset.id) === id)).filter(Boolean);
            const statusLabel = allReady ? 'All gear ready' : allReturned ? 'Needs attention' : 'Return incomplete';
            const statusTone = allReady ? 'available' : 'needs-restock';
            return (
              <article className="admin-deployment-card" key={`return-${deployment.id}`}>
                <div className="admin-deployment-title">
                  <div>
                    <strong>{deployment.person}</strong>
                    <span>{deployment.incident}{deployment.incidentOrderNumber ? ` · ${deployment.incidentOrderNumber}` : ''}</span>
                  </div>
                  <div className="admin-deployment-actions">
                    <span className={`admin-status admin-status-${statusTone}`}>{statusLabel}</span>
                    <div className="admin-day-pill">Returned {returnDateLabel(deployment.returnedAt)}</div>
                  </div>
                </div>
                <div className="admin-deployment-meta">
                  <span>{deploymentTypeLabel(deployment)}</span>
                  <span>{returnedCount}/{realAssetIds.length} physical assets returned</span>
                  {allReady && <span>All assigned gear confirmed in working order</span>}
                </div>
                <div className="admin-gear-tags">
                  {returnedAssets.map(asset => <span key={asset.id}>{asset.name}</span>)}
                  {!returnedAssets.length && <span className="admin-legacy-note">No individual physical assets recorded for this deployment</span>}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}

function GearTab({ state }) {
  const groups = buildGearStatus(state);
  return (
    <section className="admin-card">
      <div className="admin-section-head"><div><h2>Gear Status</h2><p>Actual physical assets only. Legacy LM-SET bundle records are hidden.</p></div></div>
      <div className="admin-gear-groups">
        {groups.map(group => (
          <section className="admin-gear-group" key={group.category}>
            <h3>{group.category}</h3>
            {group.rows.map(asset => (
              <div className="admin-gear-row" key={asset.id}>
                <div><strong>{asset.name}</strong><small>{asset.id}{asset.deployment ? ` · ${asset.deployment.person} @ ${asset.deployment.incident}` : ''}</small></div>
                <span className={`admin-status admin-status-${statusClass(asset.status)}`}>{asset.statusLabel}</span>
              </div>
            ))}
          </section>
        ))}
      </div>
    </section>
  );
}


function deploymentCloseout(state, deployment) {
  const assets = operationalAssets(state);
  const assetById = new Map(assets.map(asset => [normalizeAssetId(asset.id), asset]));
  const assetIds = (deployment.assetIds || []).map(normalizeAssetId).filter(id => id && !isLegacyBundleAsset(id));
  const returnEvents = (state.events || []).filter(event => event?.type === 'asset_return' && event?.deploymentId === deployment.id);
  const returnByAsset = new Map();
  returnEvents.forEach(event => {
    const id = normalizeAssetId(event.assetId);
    if (id && !returnByAsset.has(id)) returnByAsset.set(id, event);
  });
  const usage = deploymentUsageItems(state, deployment.id);
  const handoffs = (state.events || []).filter(event => {
    const type = String(event?.type || '');
    if (!type.includes('handoff')) return false;
    return [event.deploymentId, event.sourceDeploymentId, event.receivingDeploymentId]
      .some(value => String(value || '') === String(deployment.id));
  });

  const gear = assetIds.map(id => {
    const asset = assetById.get(id);
    const returned = returnByAsset.get(id);
    return {
      assetId: id,
      assetName: asset?.name || returned?.assetName || id,
      returned: Boolean(returned),
      condition: returned?.condition || asset?.condition || '',
      returnedBy: returned?.actor || returned?.returnedBy || '',
      returnedAt: returned?.timestamp || returned?.createdAt || '',
    };
  });

  return { gear, usage, handoffs };
}

async function printCloseout(state, deployment) {
  const closeout = deploymentCloseout(state, deployment);
  return openPrintableReport({
    title: 'Line Medic Deployment Closeout',
    subtitle: [
      deployment.person,
      deployment.incident,
      deployment.incidentOrderNumber,
      deploymentTypeLabel(deployment),
      `Started ${returnDateLabel(deployment.startDate || deployment.createdAt)}`,
      `Returned ${returnDateLabel(deployment.returnedAt)}`,
    ].filter(Boolean).join(' · '),
    sections: [
      {
        title: 'Deployment',
        rows: [
          `Member: ${deployment.person || '—'}`,
          `Incident: ${deployment.incident || '—'}`,
          `Order number: ${deployment.incidentOrderNumber || '—'}`,
          `Position: ${deploymentTypeLabel(deployment)}`,
          `Start: ${returnDateLabel(deployment.startDate || deployment.createdAt)}`,
          `Return: ${returnDateLabel(deployment.returnedAt)}`,
          deployment.loanerRadio ? `Loaner radio: ${deployment.loanerRadio} · ${deployment.loanerRadioReturned ? 'Returned' : 'Not marked returned'}` : 'Loaner radio: None recorded',
        ],
      },
      {
        title: 'Accountable Gear',
        headers: ['Asset', 'ID', 'Return', 'Condition', 'Returned By'],
        rows: closeout.gear.map(item => [
          item.assetName,
          item.assetId,
          item.returned ? returnDateLabel(item.returnedAt) : 'Not recorded',
          item.condition || '—',
          item.returnedBy || '—',
        ]),
      },
      {
        title: 'Supplies Used',
        headers: ['Item', 'Asset', 'Quantity'],
        rows: closeout.usage.map(item => [item.item || item.catalogId, item.assetName || item.assetId, item.quantity]),
      },
      {
        title: 'Gear Handoffs',
        headers: ['Time', 'Event', 'Actor', 'Assets'],
        rows: closeout.handoffs.map(item => [
          dateTimeLabel(item.timestamp || item.createdAt),
          String(item.type || '').replaceAll('_', ' '),
          item.actor || '',
          Array.isArray(item.assetIds) ? item.assetIds.join(', ') : (item.assetId || ''),
        ]),
      },
    ],
  });
}

async function exportCloseoutCsv(state, deployment) {
  const closeout = deploymentCloseout(state, deployment);
  const rows = [
    ...closeout.gear.map(item => ({
      record_type: 'gear',
      member: deployment.person,
      incident: deployment.incident,
      order_number: deployment.incidentOrderNumber || '',
      deployment_type: deploymentTypeLabel(deployment),
      start_date: deployment.startDate || deployment.createdAt || '',
      return_date: deployment.returnedAt || '',
      item: item.assetName,
      asset_id: item.assetId,
      quantity: '',
      condition: item.condition,
      detail: item.returned ? `Returned ${item.returnedAt || ''} by ${item.returnedBy || ''}` : 'Return not recorded',
    })),
    ...closeout.usage.map(item => ({
      record_type: 'supply_usage',
      member: deployment.person,
      incident: deployment.incident,
      order_number: deployment.incidentOrderNumber || '',
      deployment_type: deploymentTypeLabel(deployment),
      start_date: deployment.startDate || deployment.createdAt || '',
      return_date: deployment.returnedAt || '',
      item: item.item || item.catalogId,
      asset_id: item.assetId || '',
      quantity: item.quantity,
      condition: '',
      detail: item.assetName || '',
    })),
    ...closeout.handoffs.map(item => ({
      record_type: 'handoff',
      member: deployment.person,
      incident: deployment.incident,
      order_number: deployment.incidentOrderNumber || '',
      deployment_type: deploymentTypeLabel(deployment),
      start_date: deployment.startDate || deployment.createdAt || '',
      return_date: deployment.returnedAt || '',
      item: String(item.type || '').replaceAll('_', ' '),
      asset_id: item.assetId || (Array.isArray(item.assetIds) ? item.assetIds.join(' ') : ''),
      quantity: '',
      condition: '',
      detail: `${item.actor || ''} ${item.receivingName ? `→ ${item.receivingName}` : ''}`.trim(),
    })),
  ];
  if (!rows.length) {
    rows.push({
      record_type: 'deployment',
      member: deployment.person,
      incident: deployment.incident,
      order_number: deployment.incidentOrderNumber || '',
      deployment_type: deploymentTypeLabel(deployment),
      start_date: deployment.startDate || deployment.createdAt || '',
      return_date: deployment.returnedAt || '',
      item: '',
      asset_id: '',
      quantity: '',
      condition: '',
      detail: 'No detailed gear or usage rows recorded.',
    });
  }
  const safeIncident = String(deployment.incident || 'incident').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-|-$/g, '');
  return downloadCsv(`line-medic-closeout-${safeIncident || 'incident'}-${String(deployment.id || '').slice(-8)}.csv`, rows);
}

function ReportsTab({ state }) {
  const [query, setQuery] = useState('');
  const [actionError, setActionError] = useState('');
  const [busyKey, setBusyKey] = useState('');

  const runReportAction = async (key, action) => {
    setActionError('');
    setBusyKey(key);
    try {
      await action();
    } catch (error) {
      if (error?.name !== 'AbortError') {
        setActionError(error?.message || 'Unable to open or export this deployment report.');
      }
    } finally {
      setBusyKey('');
    }
  };
  const closed = useMemo(() => {
    const search = query.trim().toLowerCase();
    return (state.deployments || [])
      .filter(item => item.status === 'closed')
      .filter(item => !search || [item.person, item.incident, item.incidentOrderNumber, item.deploymentType, item.role]
        .some(value => String(value || '').toLowerCase().includes(search)))
      .sort((a, b) => (Date.parse(b.returnedAt || b.updatedAt || '') || 0) - (Date.parse(a.returnedAt || a.updatedAt || '') || 0));
  }, [state.deployments, query]);

  return (
    <section className="admin-card">
      <div className="admin-section-head">
        <div><h2>Deployment Closeout Reports</h2><p>Permanent deployment, accountable gear, supply usage, return condition, and gear-handoff history in one report.</p></div>
      </div>
      <div className="v12-admin-grid" style={{ marginBottom: 14 }}>
        <label>Search<input value={query} onChange={event => setQuery(event.target.value)} placeholder="Member, incident, order number…" /></label>
      </div>
      {actionError && <div className="v12-error" style={{ marginBottom: 14 }}>{actionError}</div>}
      {!closed.length && <div className="admin-empty">No completed deployments match this search.</div>}
      <div className="admin-deployment-list">
        {closed.map(deployment => {
          const closeout = deploymentCloseout(state, deployment);
          const usedQty = closeout.usage.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
          return (
            <article className="admin-deployment-card" key={deployment.id}>
              <div className="admin-deployment-title">
                <div>
                  <strong>{deployment.person}</strong>
                  <span>{deployment.incident}{deployment.incidentOrderNumber ? ` · ${deployment.incidentOrderNumber}` : ''}</span>
                </div>
                <div className="admin-deployment-actions">
                  <button
                    type="button"
                    className="admin-inline-action"
                    disabled={Boolean(busyKey)}
                    onClick={() => runReportAction(`print-${deployment.id}`, () => printCloseout(state, deployment))}
                  >
                    {busyKey === `print-${deployment.id}` ? 'Opening…' : 'Print / PDF'}
                  </button>
                  <button
                    type="button"
                    className="admin-inline-action"
                    disabled={Boolean(busyKey)}
                    onClick={() => runReportAction(`csv-${deployment.id}`, () => exportCloseoutCsv(state, deployment))}
                  >
                    {busyKey === `csv-${deployment.id}` ? 'Opening…' : 'CSV'}
                  </button>
                </div>
              </div>
              <div className="admin-deployment-meta">
                <span>{deploymentTypeLabel(deployment)}</span>
                <span>Returned {returnDateLabel(deployment.returnedAt)}</span>
                <span>{closeout.gear.length} gear items</span>
                <span>{usedQty} supplies used</span>
                {closeout.handoffs.length > 0 && <span>{closeout.handoffs.length} handoff event(s)</span>}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function SeasonTab({ state }) {
  const years = availableSeasonYears(state);
  const [year, setYear] = useState(years[0] || new Date().getFullYear());
  const summary = useMemo(() => buildSeasonSummary(state, year), [state, year]);

  return (
    <>
      <section className="admin-card admin-season-head">
        <div className="admin-section-head">
          <div><h2>Fire Season</h2><p>Calculated from permanent deployment and tracking-event history.</p></div>
          <label>Season
            <select value={year} onChange={event => setYear(Number(event.target.value))}>{years.map(item => <option key={item} value={item}>{item}</option>)}</select>
          </label>
        </div>
      </section>

      <div className="admin-summary-grid admin-season-summary">
        <div className="admin-stat"><span>Deployments</span><strong>{summary.deploymentCount}</strong></div>
        <div className="admin-stat"><span>Members Deployed</span><strong>{summary.uniqueMembers}</strong></div>
        <div className="admin-stat"><span>Incidents</span><strong>{summary.incidentCount}</strong></div>
        <div className="admin-stat"><span>Personnel-Days</span><strong>{summary.personnelDays}</strong></div>
        <div className="admin-stat"><span>Avg Assignment</span><strong>{summary.averageDays.toFixed(1)} d</strong></div>
        <div className="admin-stat"><span>Longest</span><strong>{summary.longestDays} d</strong></div>
      </div>

      <div className="admin-two-column">
        <section className="admin-card">
          <h2>Deployment Types</h2>
          {!summary.typeCounts.length && <div className="admin-empty">No deployments in this season.</div>}
          {summary.typeCounts.map(row => <div className="admin-table-row" key={row.type}><span><strong>{row.type}</strong><small>{row.count} deployments</small></span><strong>{row.count}</strong></div>)}
        </section>

        <section className="admin-card">
          <h2>Members</h2>
          {!summary.members.length && <div className="admin-empty">No member deployment history in this season.</div>}
          {summary.members.map(row => <div className="admin-table-row" key={row.person}><span><strong>{row.person}</strong><small>{row.deployments} deployments</small></span><strong>{row.days} days</strong></div>)}
        </section>
      </div>

      <div className="admin-two-column">
        <section className="admin-card">
          <h2>Incidents Supported</h2>
          {!summary.incidents.length && <div className="admin-empty">No incident history in this season.</div>}
          {summary.incidents.map(row => <div className="admin-table-row" key={row.incident}><span><strong>{row.incident}</strong><small>{row.deployments} deployments</small></span><strong>{row.days} days</strong></div>)}
        </section>

        <section className="admin-card">
          <h2>Most Used Supplies</h2>
          {!summary.supplies.length && <div className="admin-empty">No in-deployment supply usage has been recorded for this season yet.</div>}
          {summary.supplies.slice(0, 20).map(row => <div className="admin-table-row" key={`${row.catalogId}-${row.item}`}><span>{row.item}</span><strong>×{row.quantity}</strong></div>)}
        </section>
      </div>

      <section className="admin-card">
        <h2>Gear Utilization</h2>
        {!summary.gear.length && <div className="admin-empty">No individual physical-asset deployment history in this season.</div>}
        {summary.gear.slice(0, 40).map(row => <div className="admin-table-row" key={row.assetId}><span><strong>{row.assetName}</strong><small>{row.assetId} · {row.deployments} deployments</small></span><strong>{row.days} days</strong></div>)}
      </section>
    </>
  );
}

export default function DeploymentDashboard({ profile }) {
  const { state, sync } = useTrackingStore();
  const [tab, setTab] = useState(initialDashboardTab);
  const [editingDeploymentId, setEditingDeploymentId] = useState('');

  if (!profile?.is_admin) {
    return <div className="admin-root"><main className="admin-page"><section className="admin-card admin-denied"><h1>Admin access required</h1><button type="button" onClick={() => window.location.assign('/')}>Back</button></section></main></div>;
  }

  const editingDeployment = state.deployments.find(item => item.id === editingDeploymentId && item.status !== 'closed') || null;

  const selectTab = next => {
    setTab(next);
    if (next !== 'live') setEditingDeploymentId('');
    const url = new URL(window.location.href);
    url.searchParams.set('tab', next);
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  };

  return (
    <div className="admin-root">
      <header className="admin-topbar">
        <div><h1>Deployment Dashboard</h1><p>Live Line Medic operations and season history · {sync.status === 'synced' ? 'Synced' : sync.status}</p></div>
        <button type="button" onClick={() => window.location.assign('/admin')}>Admin Home</button>
      </header>
      <nav className="admin-tabs" aria-label="Deployment dashboard sections">
        <button type="button" className={tab === 'live' ? 'active' : ''} onClick={() => selectTab('live')}>Live</button>
        <button type="button" className={tab === 'gear' ? 'active' : ''} onClick={() => selectTab('gear')}>Gear</button>
        <button type="button" className={tab === 'history' ? 'active' : ''} onClick={() => selectTab('history')}>Asset History</button>
        <button type="button" className={tab === 'reports' ? 'active' : ''} onClick={() => selectTab('reports')}>Reports</button>
        <button type="button" className={tab === 'season' ? 'active' : ''} onClick={() => selectTab('season')}>Season</button>
      </nav>
      <main className="admin-page">
        {tab === 'live' && editingDeployment && (
          <DeploymentGearEditor deployment={editingDeployment} state={state} onClose={() => setEditingDeploymentId('')} />
        )}
        {tab === 'live' && <LiveTab state={state} onEditGear={deployment => setEditingDeploymentId(deployment.id)} />}
        {tab === 'gear' && <GearTab state={state} />}
        {tab === 'history' && <AssetHistory state={state} />}
        {tab === 'reports' && <ReportsTab state={state} />}
        {tab === 'season' && <SeasonTab state={state} />}
      </main>
    </div>
  );
}
